import {
  Bell,
  CheckCircle2,
  ChevronRight,
  KeyRound,
  Laptop,
  LogOut,
  Monitor,
  Server,
  Settings,
  ShieldCheck,
  Smartphone,
  UserRound,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { AccountSecurityCenter } from '../components/AccountSecurityCenter';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { useMonitoring } from '../context/MonitoringContext';
import { formatCount, formatFullDateTime, formatRole } from '../utils/format';

const initials = (value: string) => value.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

export function ProfilePage() {
  const { profile, isLoading, logout } = useAuth();
  const { connectionState, snapshot } = useMonitoring();
  const navigate = useNavigate();

  if (!profile) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="page-stack profile-page">
      <PageHeader eyebrow="Учётная запись" title="Профиль" description="Личные данные, доступ и безопасность сессии" />

      <section className="profile-hero">
        <span className="avatar avatar--large" aria-hidden="true">{initials(profile.displayName)}</span>
        <div className="profile-hero__identity">
          <div><h2>{profile.displayName}</h2></div>
          <p>@{profile.login} · {formatRole(profile.role)}</p>
          <ConnectionBadge state={connectionState} />
        </div>
        <button className="button button--secondary profile-logout" type="button" onClick={() => void handleLogout()} disabled={isLoading}>
          <LogOut size={17} /> Выйти
        </button>
      </section>

      <div className="profile-grid">
        <section className="surface-card profile-info-card">
          <div className="section-heading"><div><span className="eyebrow">Аккаунт</span><h2>Личные данные</h2></div><UserRound size={19} /></div>
          <dl className="info-list info-list--spaced">
            <div><dt>Отображаемое имя</dt><dd>{profile.displayName}</dd></div>
            <div><dt>Логин</dt><dd className="mono">{profile.login}</dd></div>
            <div><dt>Роль</dt><dd><span className="role-badge"><ShieldCheck size={14} /> {formatRole(profile.role)}</span></dd></div>
            <div><dt>Язык</dt><dd>Русский</dd></div>
            <div><dt>Часовой пояс</dt><dd>{Intl.DateTimeFormat().resolvedOptions().timeZone}</dd></div>
            <div><dt>Создан</dt><dd>{formatFullDateTime(profile.createdAtUtc)}</dd></div>
          </dl>
          <Link className="button button--secondary button--full" to="/settings"><Settings size={17} /> Настройки профиля</Link>
        </section>

        <section className="surface-card security-card">
          <div className="section-heading"><div><span className="eyebrow">Защита</span><h2>Безопасность</h2></div><ShieldCheck size={19} /></div>
          <div className="security-check-list">
            <div><span className="security-check-list__icon is-ok"><KeyRound size={18} /></span><div><strong>Приложение-аутентификатор</strong><span>{profile.hasTwoFactor ? 'Подключено' : 'Требуется настройка'}</span></div><CheckCircle2 size={18} className="success-text" /></div>
            <div><span className="security-check-list__icon is-ok"><Laptop size={18} /></span><div><strong>Web-сессия</strong><span>Защищена HttpOnly cookie и CSRF-токеном</span></div><CheckCircle2 size={18} className="success-text" /></div>
            <div><span className="security-check-list__icon is-ok"><Smartphone size={18} /></span><div><strong>Коды восстановления</strong><span>Одноразовые резервные коды для входа</span></div><CheckCircle2 size={18} className="success-text" /></div>
          </div>
        </section>

        <AccountSecurityCenter />

        <section className="surface-card profile-shortcuts">
          <div className="section-heading"><div><span className="eyebrow">Быстрый доступ</span><h2>Разделы</h2></div></div>
          <Link to="/servers"><span className="shortcut-icon"><Server size={18} /></span><div><strong>Серверы</strong><span>{snapshot?.devices.filter((device) => device.type === 'server').length ?? 0} под мониторингом</span></div><ChevronRight size={17} /></Link>
          <Link to="/computers"><span className="shortcut-icon"><Monitor size={18} /></span><div><strong>Компьютеры</strong><span>{formatCount(snapshot?.devices.filter((device) => device.type === 'computer').length ?? 0, 'рабочая станция', 'рабочие станции', 'рабочих станций')}</span></div><ChevronRight size={17} /></Link>
          <Link to="/events"><span className="shortcut-icon"><Bell size={18} /></span><div><strong>События</strong><span>{snapshot?.events.filter((event) => !event.acknowledged).length ?? 0} новых</span></div><ChevronRight size={17} /></Link>
          <Link to="/settings"><span className="shortcut-icon"><Settings size={18} /></span><div><strong>Настройки</strong><span>Подключение и интерфейс</span></div><ChevronRight size={17} /></Link>
        </section>
      </div>
    </div>
  );
}
